import { MaterialSymbols } from 'material-design-icons-literal-types';
import {
  Bell,
  Box,
  ClipboardList,
  Contact,
  FileText,
  Layers,
  LayoutDashboard,
  Library,
  ListChecks,
  ClipboardCheck,
  MapPin,
  Network,
  NotebookPen,
  Presentation,
  Radio,
  Siren,
  type LucideIcon,
} from 'lucide-react';

export enum CorporateViewTabs {
  Notifications = 'Notifications',
  Events = 'Events',
  Reports = 'Reports',
  Incidents = 'Incidents',
  Actions = 'Actions',
  ReviewQueue = 'ReviewQueue',
  AORs = 'AORs',
  Resources = 'Resources',
  Layers = 'Layers',
  SitStat = 'SitStat',
  PlanLibrary = 'PlanLibrary',
  RosterTemplates = 'RosterTemplates',
  Presentation = 'Presentation',
  Notes = 'Notes',
  ContactLists = 'ContactLists',
  FocTracker = 'FocTracker',
}

export const HUB_TAB_ICONS: Record<CorporateViewTabs, LucideIcon> = {
  [CorporateViewTabs.Notifications]: Bell,
  [CorporateViewTabs.Events]: Radio,
  [CorporateViewTabs.Reports]: FileText,
  [CorporateViewTabs.Incidents]: Siren,
  [CorporateViewTabs.Actions]: ListChecks,
  [CorporateViewTabs.ReviewQueue]: ClipboardCheck,
  [CorporateViewTabs.AORs]: MapPin,
  [CorporateViewTabs.Resources]: Box,
  [CorporateViewTabs.Layers]: Layers,
  [CorporateViewTabs.SitStat]: LayoutDashboard,
  [CorporateViewTabs.PlanLibrary]: Library,
  [CorporateViewTabs.RosterTemplates]: Network,
  [CorporateViewTabs.Presentation]: Presentation,
  [CorporateViewTabs.Notes]: NotebookPen,
  [CorporateViewTabs.ContactLists]: Contact,
  [CorporateViewTabs.FocTracker]: ClipboardList,
};

export const TabNameToIconName: Record<CorporateViewTabs, MaterialSymbols> = {
  [CorporateViewTabs.Notifications]: 'notifications',
  [CorporateViewTabs.Events]: 'event',
  [CorporateViewTabs.Reports]: 'docs',
  [CorporateViewTabs.Incidents]: 'warning_amber',
  [CorporateViewTabs.Actions]: 'checklist',
  [CorporateViewTabs.ReviewQueue]: 'rate_review',
  [CorporateViewTabs.AORs]: 'location_on',
  [CorporateViewTabs.Resources]: 'package_2',
  [CorporateViewTabs.Layers]: 'stacks',
  [CorporateViewTabs.SitStat]: 'dashboard',
  [CorporateViewTabs.PlanLibrary]: 'library_books',
  [CorporateViewTabs.RosterTemplates]: 'account_tree',
  [CorporateViewTabs.Presentation]: 'slideshow',
  [CorporateViewTabs.Notes]: 'edit_note',
  [CorporateViewTabs.ContactLists]: 'contacts',
  [CorporateViewTabs.FocTracker]: 'assignment',
};

export const HUB_PRIMARY_TABS: CorporateViewTabs[] = [
  CorporateViewTabs.Notifications,
  CorporateViewTabs.Events,
  CorporateViewTabs.Reports,
];

export const HUB_MORE_TABS: CorporateViewTabs[] = [
  CorporateViewTabs.Incidents,
  CorporateViewTabs.Actions,
  CorporateViewTabs.ReviewQueue,
  CorporateViewTabs.AORs,
  CorporateViewTabs.Resources,
  CorporateViewTabs.Layers,
  CorporateViewTabs.SitStat,
  CorporateViewTabs.PlanLibrary,
  CorporateViewTabs.RosterTemplates,
  CorporateViewTabs.Presentation,
  CorporateViewTabs.Notes,
  CorporateViewTabs.ContactLists,
  CorporateViewTabs.FocTracker,
];

export const INCIDENT_MORE_TABS: CorporateViewTabs[] = [
  CorporateViewTabs.Actions,
  CorporateViewTabs.Resources,
  CorporateViewTabs.Layers,
];

export const MORE_TAB_LABELS: Partial<Record<CorporateViewTabs, string>> = {
  [CorporateViewTabs.Incidents]: 'Incidents',
  [CorporateViewTabs.Actions]: 'Tasks',
  [CorporateViewTabs.ReviewQueue]: 'Review Queue',
  [CorporateViewTabs.AORs]: 'AORs',
  [CorporateViewTabs.Resources]: 'Assets',
  [CorporateViewTabs.Layers]: 'Map Layers',
  [CorporateViewTabs.SitStat]: 'SitStat',
  [CorporateViewTabs.PlanLibrary]: 'Plan Library',
  [CorporateViewTabs.RosterTemplates]: 'Roster Templates',
  [CorporateViewTabs.Presentation]: 'Presentations',
  [CorporateViewTabs.Notes]: 'Notes',
  [CorporateViewTabs.ContactLists]: 'Contact Lists',
  [CorporateViewTabs.FocTracker]: 'FOC Tracker',
};

export function moreTabLabel(
  tab: CorporateViewTabs,
  aorPlural = 'AORs'
): string {
  if (tab === CorporateViewTabs.AORs) return aorPlural;
  return MORE_TAB_LABELS[tab] ?? tab;
}

export function getDefaultHubTab(
  hideNotifications: boolean
): CorporateViewTabs {
  return hideNotifications
    ? CorporateViewTabs.Events
    : CorporateViewTabs.Notifications;
}

export type HubNavItem = {
  tab: CorporateViewTabs;
  label: string;
  icon: LucideIcon;
  to: string;
};

export function getHubNavItems({
  hideNotifications,
  hideActions,
  hideReviewQueue,
  hideAorsTab,
  hideContactLists,
  hideFocTracker,
  aorPlural = 'AORs',
}: {
  hideNotifications: boolean;
  hideActions: boolean;
  hideReviewQueue?: boolean;
  hideAorsTab?: boolean;
  hideContactLists?: boolean;
  hideFocTracker?: boolean;
  aorPlural?: string;
}): HubNavItem[] {
  return [...HUB_PRIMARY_TABS, ...HUB_MORE_TABS]
    .filter((tab) => {
      if (hideNotifications && tab === CorporateViewTabs.Notifications) {
        return false;
      }
      if (hideActions && tab === CorporateViewTabs.Actions) {
        return false;
      }
      if (hideReviewQueue && tab === CorporateViewTabs.ReviewQueue) {
        return false;
      }
      if (hideAorsTab && tab === CorporateViewTabs.AORs) {
        return false;
      }
      if (hideContactLists && tab === CorporateViewTabs.ContactLists) {
        return false;
      }
      if (hideFocTracker && tab === CorporateViewTabs.FocTracker) {
        return false;
      }
      return true;
    })
    .map((tab) => ({
      tab,
      label: moreTabLabel(tab, aorPlural),
      icon: HUB_TAB_ICONS[tab],
      to: `/hub?openTab=${tab}`,
    }));
}

export function isHubNavItemActive(
  tab: CorporateViewTabs,
  pathname: string,
  search: string,
  hideNotifications: boolean
): boolean {
  if (pathname !== '/hub') {
    return false;
  }
  const openTab = new URLSearchParams(search).get('openTab');
  const current = openTab || getDefaultHubTab(hideNotifications);
  return current === tab;
}
